import SEO from "@/components/SEO";
import { Reveal, Overline } from "@/components/Reveal";
import { SITE } from "@/lib/site";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    body: [
      "When you request a consultation or contact us through this website, we collect the information you provide, which may include your name, phone number, email address, city, project type, estimated budget, project details, and any photos of your space that you choose to upload.",
      "We may also collect basic technical information automatically when you visit this website, such as your browser type, device type, and pages visited, to help us understand how the site is used.",
    ],
  },
  {
    title: "2. How We Use Your Information",
    body: [
      "We use the information you provide to respond to your consultation request, discuss your project, prepare estimates or designs, and communicate with you about our services.",
      "We may also use your contact information to follow up on your enquiry or to share information about our services that may be relevant to your project. You can ask us to stop at any time.",
    ],
  },
  {
    title: "3. How We Protect Your Information",
    body: [
      "We take reasonable administrative and technical measures to protect your personal information against unauthorized access, loss, or misuse. Project photos and contact details you submit are stored securely and are only accessible to our team.",
    ],
  },
  {
    title: "4. Sharing of Information",
    body: [
      "We do not sell, rent, or trade your personal information. We may share information with trusted trades, suppliers, or service providers only as needed to plan or complete your project, or when required by law.",
    ],
  },
  {
    title: "5. Cookies and Analytics",
    body: [
      "This website may use basic cookies or analytics tools to help us understand visitor traffic and improve the site. You can disable cookies in your browser settings at any time without affecting your ability to browse this website.",
    ],
  },
  {
    title: "6. Your Rights",
    body: [
      "Under Canada's Personal Information Protection and Electronic Documents Act (PIPEDA), you have the right to access the personal information we hold about you, request corrections, or ask us to delete it. To make a request, contact us using the details below.",
    ],
  },
  {
    title: "7. Changes to This Policy",
    body: [
      "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.",
    ],
  },
];

export default function Privacy() {
  return (
    <main data-testid="privacy-page" className="pt-36 md:pt-44 pb-24 md:pb-32">
      <SEO
        title={`Privacy Policy | ${SITE.name}`}
        description={`Privacy Policy for ${SITE.name} — how we collect, use, and protect your personal information when you request a consultation for custom kitchen cabinets or renovations in the GTA.`}
      />
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Reveal>
          <Overline>Legal</Overline>
          <h1 className="mt-5 font-heading text-4xl md:text-6xl tracking-tight leading-[1.08]">
            Privacy <span className="italic text-primary">Policy</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            {SITE.name} respects your privacy. This policy explains what information we collect through this website, how we use it, and how we protect it.
          </p>
          <p className="mt-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Effective date: August 2026
          </p>
        </Reveal>

        <div className="mt-14 space-y-10">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.04}>
              <section data-testid={`privacy-section-${i + 1}`} className="border-t border-border pt-8">
                <h2 className="font-heading text-2xl tracking-tight">{s.title}</h2>
                {s.body.map((p, j) => (
                  <p key={j} className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                    {p}
                  </p>
                ))}
              </section>
            </Reveal>
          ))}

          <Reveal delay={0.05}>
            <section data-testid="privacy-contact" className="border-t border-border pt-8">
              <h2 className="font-heading text-2xl tracking-tight">Contact Us</h2>
              <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                If you have questions about this Privacy Policy or how your information is handled, contact us:
              </p>
              <ul className="mt-4 space-y-2 text-sm md:text-base text-foreground/85">
                <li>{SITE.name} — {SITE.address}</li>
                <li>Phone: <a href={SITE.phoneHref} data-testid="privacy-phone" className="text-primary hover:underline">{SITE.phone}</a></li>
                <li>Email: <a href={`mailto:${SITE.email}`} data-testid="privacy-email" className="text-primary hover:underline">{SITE.email}</a></li>
              </ul>
            </section>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
